/**
 * Inline icons.
 *
 * An icon package would ship hundreds of glyphs to deliver the twenty this app
 * uses, so the set is written out here. Everything is a 24x24 viewBox and
 * inherits `currentColor`, which keeps the icons honest in every context they
 * appear in (muted rows, accent chips, white-on-video controls).
 */

import type { ReactElement } from 'react';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const ICONS = {
  play: <path d="M8 5.2v13.6L19 12 8 5.2z" fill="currentColor" />,
  pause: <path d="M6.5 5h3.5v14H6.5zM14 5h3.5v14H14z" fill="currentColor" />,
  star: (
    <path
      d="M12 3.6l2.5 5.1 5.6.8-4 4 .9 5.6-5-2.6-5 2.6.9-5.6-4-4 5.6-.8L12 3.6z"
      {...STROKE}
    />
  ),
  starFilled: (
    <path
      d="M12 3.6l2.5 5.1 5.6.8-4 4 .9 5.6-5-2.6-5 2.6.9-5.6-4-4 5.6-.8L12 3.6z"
      fill="currentColor"
    />
  ),
  chevronLeft: <path d="M14.5 5.5 8 12l6.5 6.5" {...STROKE} />,
  chevronRight: <path d="M9.5 5.5 16 12l-6.5 6.5" {...STROKE} />,
  chevronDown: <path d="M6 9.5 12 15.5 18 9.5" {...STROKE} />,
  search: (
    <g {...STROKE}>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="M15.5 15.5 20 20" />
    </g>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" {...STROKE} />,
  check: <path d="M4.5 12.5 9.5 17.5 19.5 6.5" {...STROKE} />,
  refresh: (
    <g {...STROKE}>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4v4.5h-4.5" />
    </g>
  ),
  trash: (
    <g {...STROKE}>
      <path d="M4.5 6.5h15" />
      <path d="M9.5 6.5V4h5v2.5" />
      <path d="M6.5 6.5 7.5 20h9l1-13.5" />
      <path d="M10.5 10v6M13.5 10v6" />
    </g>
  ),
  pencil: (
    <g {...STROKE}>
      <path d="M4 20h4L19.2 8.8a2.1 2.1 0 0 0-3-3L5 17l-1 3z" />
    </g>
  ),
  plus: <path d="M12 5v14M5 12h14" {...STROKE} />,
  volume: (
    <g {...STROKE}>
      <path d="M4.5 9.5h3l4.5-3.8v12.6L7.5 14.5h-3z" />
      <path d="M15.5 9.4a3.6 3.6 0 0 1 0 5.2" />
      <path d="M18 7a7 7 0 0 1 0 10" />
    </g>
  ),
  volumeOff: (
    <g {...STROKE}>
      <path d="M4.5 9.5h3l4.5-3.8v12.6L7.5 14.5h-3z" />
      <path d="M16 10l4 4M20 10l-4 4" />
    </g>
  ),
  fullscreen: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" {...STROKE} />,
  fullscreenExit: <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" {...STROKE} />,
  tv: (
    <g {...STROKE}>
      <rect x="3" y="5" width="18" height="12.5" rx="2" />
      <path d="M8.5 21h7" />
    </g>
  ),
  film: (
    <g {...STROKE}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M8 4.5v15M16 4.5v15M3.5 12h17" />
    </g>
  ),
  stack: (
    <g {...STROKE}>
      <rect x="8" y="3.5" width="12.5" height="11" rx="1.6" />
      <path d="M4.5 7.5v11a2 2 0 0 0 2 2h10" />
    </g>
  ),
  globe: (
    <g {...STROKE}>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M3.7 12h16.6" />
      <path d="M12 3.7c4.5 4.6 4.5 12.1 0 16.6-4.5-4.5-4.5-12 0-16.6z" />
    </g>
  ),
  sliders: (
    <g {...STROKE}>
      <path d="M4 7h16M4 12h16M4 17h16" />
      <circle cx="9" cy="7" r="2" fill="var(--bg-0)" />
      <circle cx="15" cy="12" r="2" fill="var(--bg-0)" />
      <circle cx="8" cy="17" r="2" fill="var(--bg-0)" />
    </g>
  ),
  signOut: (
    <g {...STROKE}>
      <path d="M14 4h5a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-5" />
      <path d="M14 12H3.5M7.5 8 3.5 12l4 4" />
    </g>
  ),
  arrowUp: <path d="M12 19V5m0 0-6 6m6-6 6 6" {...STROKE} />,
  arrowDown: <path d="M12 5v14m0 0 6-6m-6 6-6-6" {...STROKE} />,
  link: (
    <g {...STROKE}>
      <path d="M10.5 13.5a4.6 4.6 0 0 0 6.5 0l2-2a4.6 4.6 0 0 0-6.5-6.5l-1 1" />
      <path d="M13.5 10.5a4.6 4.6 0 0 0-6.5 0l-2 2a4.6 4.6 0 0 0 6.5 6.5l1-1" />
    </g>
  ),
  home: (
    <g {...STROKE}>
      <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
    </g>
  ),
  people: (
    <g {...STROKE}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c.4-3.2 2.6-5 5.5-5s5.1 1.8 5.5 5" />
      <circle cx="16.5" cy="8.5" r="2.4" />
      <path d="M16 14.2c2.4.3 4.2 1.8 4.6 4.3" />
    </g>
  ),
  settings: (
    <g {...STROKE}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3.5v2.4M12 18.1v2.4M20.5 12h-2.4M5.9 12H3.5M17.7 6.3l-1.7 1.7M8 16l-1.7 1.7M17.7 17.7 16 16M8 8 6.3 6.3" />
    </g>
  ),
  alertTriangle: (
    <g {...STROKE}>
      <path d="M12 4 21 19.5H3L12 4z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
    </g>
  ),
  eye: (
    <g {...STROKE}>
      <path d="M3 12s3.5-6.5 9-6.5S21 12 21 12s-3.5 6.5-9 6.5S3 12 3 12z" />
      <circle cx="12" cy="12" r="2.6" />
    </g>
  ),
  // `satisfies` keeps the keys literal, so IconName is a union and a typo in a
  // call site is a compile error rather than a blank square.
} satisfies Record<string, ReactElement>;

export type IconName = keyof typeof ICONS;

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 18, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      {ICONS[name]}
    </svg>
  );
}
